// app/javascript/components/ColumnsConstructor/ColumnsTable.jsx
import React from 'react';
import { 
  Edit2, 
  Trash2, 
  Eye, 
  EyeOff, 
  BarChart3, 
  Layers 
} from 'lucide-react';
import { 
  getColumnTypeIcon, 
  getColumnTypeBadgeClass 
} from './columnsUtils';

// Компонент для отображения иконки типа колонки
const ColumnTypeIcon = ({ columnType }) => {
  const iconName = getColumnTypeIcon(columnType);
  
  const iconComponents = {
    BarChart3: BarChart3,
    Settings: Edit2, // Используем Edit2 вместо Settings для простоты
    AlertTriangle: Edit2, // Используем Edit2 как заглушку
    Layers: Layers
  };
  
  const IconComponent = iconComponents[iconName] || BarChart3;
  return <IconComponent className="h-4 w-4" />;
};

// Компонент таблицы Chart Groups
export const ChartGroupsTable = ({ 
  chartGroups, 
  onEdit, 
  onDelete, 
  isDeleting, 
  isSaving, 
  userStatus 
}) => {
  if (chartGroups.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Layers className="h-5 w-5" />
        Chart Groups
        {userStatus.is_personalized && (
          <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">Personal</span>
        )}
      </h2>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-indigo-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Key
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Group Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Metrics
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Color
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {chartGroups.map((group) => (
              <tr key={group.key} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{group.key}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-indigo-500" />
                    <span className="text-sm text-gray-900">{group.group_name || group.label}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {group.metrics && group.metrics.length > 0 ? (
                      <div className="space-y-1">
                        {group.metrics.map(metric => {
                          const color = group.colors?.[metric] || '#666';
                          return (
                            <div key={metric} className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full border border-gray-300" 
                                style={{ backgroundColor: color }}
                              ></div>
                              <span className="text-xs">{metric}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-xs">No metrics</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded border border-gray-300"
                      style={{ backgroundColor: group.chart_color }}
                    ></div>
                    <span className="text-xs text-gray-600">{group.chart_color}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onEdit(group)}
                      disabled={isSaving}
                      className={`p-1 rounded transition-colors ${
                        isSaving 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50'
                      }`}
                      title={isSaving ? 'Saving...' : 'Edit chart group'}
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDelete(group)}
                      disabled={isDeleting}
                      className={`p-1 rounded transition-colors ${
                        isDeleting 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                      }`}
                      title={isDeleting ? 'Deleting...' : 'Delete chart group'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Компонент таблицы обычных колонок
export const ColumnsTable = ({ 
  columns, 
  onEdit, 
  onDelete, 
  onToggleVisibility, 
  onToggleChart, 
  isDeleting, 
  isSaving, 
  userStatus 
}) => {
  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <BarChart3 className="h-5 w-5" />
        Columns
        {userStatus.is_personalized && (
          <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">Personal</span>
        )}
      </h2>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Key
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Label
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Color
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Visible
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Chart
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {columns.map((column) => (
              <tr key={column.key} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{column.key}</div>
                  {column.description && (
                    <div 
                      className="text-xs text-gray-500 max-w-xs truncate" 
                      title={column.description}
                    >
                      {column.description.length > 50 
                        ? `${column.description.substring(0, 50)}...` 
                        : column.description
                      }
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center gap-2">
                    <ColumnTypeIcon columnType={column.column_type} />
                    {column.label}
                    {column.has_personal_changes && (
                      <span className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded">
                        Modified
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getColumnTypeBadgeClass(column.column_type)}`}>
                    {column.column_type.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded border border-gray-300"
                      style={{ backgroundColor: column.chart_color }}
                    ></div>
                    <span className="text-xs text-gray-600">{column.chart_color}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button
                    onClick={() => onToggleVisibility(column)}
                    disabled={isSaving}
                    className={`p-1 rounded transition-colors ${
                      isSaving 
                        ? 'cursor-not-allowed' 
                        : 'hover:bg-gray-100'
                    }`}
                    title={`${column.is_visible ? 'Hide' : 'Show'} column`}
                  >
                    {column.is_visible ? (
                      <Eye className="h-4 w-4 text-green-500" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button
                    onClick={() => onToggleChart(column)}
                    disabled={isSaving}
                    className={`p-1 rounded transition-colors ${
                      isSaving 
                        ? 'cursor-not-allowed' 
                        : 'hover:bg-gray-100'
                    }`}
                    title={`${column.show_on_chart ? 'Remove from' : 'Add to'} chart`}
                  >
                    {column.show_on_chart ? (
                      <BarChart3 className="h-4 w-4 text-blue-500" />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onEdit(column)}
                      disabled={isSaving}
                      className={`p-1 rounded transition-colors ${
                        isSaving 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-blue-600 hover:text-blue-900 hover:bg-blue-50'
                      }`}
                      title={isSaving ? 'Saving...' : 'Edit column'}
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    {!column.is_system && (
                      <button
                        onClick={() => onDelete(column)}
                        disabled={isDeleting}
                        className={`p-1 rounded transition-colors ${
                          isDeleting 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                        }`}
                        title={isDeleting ? 'Deleting...' : 'Delete column'}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};